import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Asignacion } from '../models/asignacion';
import { CrearVlanParametros } from '../models/crear-vlan-parametros';
import { AsignacionManualDto } from '../models/asignacion-manual-dto';
import { ParametrosConsultarAsignacion } from '../models/parametros-consultar-asignacion';
import { ExportarExcelAsignacionesDto } from '../models/exportar-asignaciones-dto';
import { Solicitud } from '../models/solicitud';
import { HistoricoDireccionesDto } from '../models/historico-direcciones-dto';
import { ConsultaVlansDto } from '../models/consulta-vlans-dto';
import { AsignacionManualSedeDto } from '../models/asignacion-manual-sede-dto';
import { CrearSedeDto } from '../models/crear-sede-dto';
import { Vlan } from '../models/vlan';
import { SuperNet } from '../models/supernet';

@Injectable()
export class AdministradorIpService {

  private administradorEndPoint: string = environment.administradorIpEndPoint;
  private httpHeaders = new HttpHeaders({ 'content-type': 'application/json' });

  constructor(private http: HttpClient) { }

  // Consume el servicio que guarda una lista de asignaciones en base a solicitudes
  guardarAsignaciones(solicitudes: Solicitud[]): Observable<Asignacion[]> {
    return this.http.post(this.administradorEndPoint + 'asignardirecciones',
      solicitudes, { responseType: 'json' }).pipe(
        map((resultado) => resultado as Asignacion[])
      );
  }

  // Consume el servicio que consulta las asignaciones para liberar
  buscarAsignacionesParaLiberar(ip: string, url: string,
    servidor: string, usuario: string): Observable<Asignacion[]> {
    return this.http.get(this.administradorEndPoint + 'asignacionesaliberar/' + ip +
      '/' + url + '/' + servidor + '/' + usuario).pipe(
        map((resultado) => resultado as Asignacion[])
      );
  }

  // Consume el servicio que inactiva las asignaciones y libera direciones ip
  liberarDiercciones(asignaciones: Asignacion[]): Observable<Asignacion[]> {
    return this.http.post(this.administradorEndPoint + 'liberardiercciones',
      asignaciones, { responseType: 'json' }).pipe(
        map((resultado) => resultado as Asignacion[])
      );
  }

  // Consuume el servicio que consulta las asignaciones para la pantalla conultar Asignaciones
  consultarAsignacionesByParametros(parametros: ParametrosConsultarAsignacion): Observable<Asignacion[]> {
    return this.http.post(this.administradorEndPoint + 'consultarasignaciones',
      parametros, { responseType: 'json' }).pipe(
        map((resultado) => resultado as Asignacion[])
      );
  }

  // Consume el servicio que descarga el xlsx enviando el listado de asignaciones
  public async exportarAsignaciones(listaExportar: ExportarExcelAsignacionesDto[]): Promise<Blob> {
    const file = await this.http.post<Blob>(this.administradorEndPoint + 'exportarasignaciones',
      listaExportar, { responseType: 'blob' as 'json' }).toPromise();
    return file;
  }

  // servicio que consulta las ip tenga o no asignacion
  consultarDireccionesIpAsignacionManual(idVlan: number, direccion: string): Observable<AsignacionManualDto[]> {
    return this.http.get(this.administradorEndPoint + 'listarasignacionmanual/' +
      idVlan + '/' + direccion).pipe(
        map((resultado) => resultado as AsignacionManualDto[])
      );
  }

  // servicio que consulta las ip tenga o no asignacion
  consultarDireccionesIpAsignacionManualSede(idVlan: number, direccion: string): Observable<AsignacionManualSedeDto[]> {
    return this.http.get(this.administradorEndPoint + 'sedes/listarasignacionmanual/' +
      idVlan + '/' + direccion).pipe(
        map((resultado) => resultado as AsignacionManualSedeDto[])
      );
  }

  // Consume el servicio que inactiva las asignaciones y libera direciones ip
  guardarAsignacionManual(asignacion: AsignacionManualDto): Observable<AsignacionManualDto> {
    return this.http.post(this.administradorEndPoint + 'guardarasignacionmanual',
      asignacion, { responseType: 'json' }).pipe(
        map((resultado) => resultado as AsignacionManualDto)
      );
  }

  // Consume el servicio que activa una Vlan
  crearVlan(parametros: CrearVlanParametros): Observable<CrearVlanParametros> {
    return this.http.post(this.administradorEndPoint + 'crearvlan',
      parametros, { responseType: 'json' }).pipe(
        map((resultado) => resultado as CrearVlanParametros)
      );
  }

  // Consume el servicio que consulta el historico de asignaciones de una direccion
  consultarHistoricoDirecciones(direccion: string, desde: string,
    hasta: string): Observable<HistoricoDireccionesDto[]> {
    return this.http.get(this.administradorEndPoint + 'historico/' + direccion
      + '/' + desde + '/' + hasta).pipe(
        map((resultado) => resultado as HistoricoDireccionesDto[])
      );
  }

  // Consume el servicio que reactiva una asignacion
  // Se envia un HistoricoDireccionesDto que contiene el id de la asignacion a reactivar
  reactivarAsignacion(historico: HistoricoDireccionesDto): Observable<HistoricoDireccionesDto> {
    return this.http.put(this.administradorEndPoint + 'reactivar/asignacion',
      historico, { responseType: 'json' }).pipe(
        map((resultado) => resultado as HistoricoDireccionesDto)
      );
  }

  // Consume el servicio que descarga el xlsx del historico de direcciones
  public async exportarHistoricoDirecciones(listaExportar: HistoricoDireccionesDto[]): Promise<Blob> {
    const file = await this.http.post<Blob>(this.administradorEndPoint + '/exportar/historico',
      listaExportar, { responseType: 'blob' as 'json' }).toPromise();
    return file;
  }

  editarVlan(vlanDto: ConsultaVlansDto): Observable<ConsultaVlansDto> {
    return this.http.post(this.administradorEndPoint + 'editar/vlan', vlanDto,
      { responseType: 'json' }).pipe(
        map((resultado) => resultado as ConsultaVlansDto)
      );
  }

  // Consume el servicio que crear una Vlan sin rango predefinido
  crearVlanSinRango(parametros: CrearVlanParametros): Observable<CrearVlanParametros> {
    return this.http.post(this.administradorEndPoint + 'nuevavlan',
      parametros, { responseType: 'json' }).pipe(
        map((resultado) => resultado as CrearVlanParametros)
      );
  }

  editarAsignacionSede(asignacion: AsignacionManualSedeDto): Observable<AsignacionManualSedeDto> {
    return this.http.post(this.administradorEndPoint + 'asignacionsede', asignacion,
      { responseType: 'json' }).pipe(
        map((resultado) => resultado as AsignacionManualSedeDto)
      );
  }

  liberarIpSede(asignacion: AsignacionManualSedeDto): Observable<AsignacionManualSedeDto> {
    return this.http.post(this.administradorEndPoint + 'sede/liberarIP', asignacion,
      { responseType: 'json' }).pipe(
        map((resultado) => resultado as AsignacionManualSedeDto)
      );
  }
  liberarVlan(vlanDto: ConsultaVlansDto): Observable<ConsultaVlansDto> {
    return this.http.post(this.administradorEndPoint + 'liberar/vlan', vlanDto,
      { responseType: 'json' }).pipe(
        map((resultado) => resultado as ConsultaVlansDto)
      );
  }
  crearAsociacionVlanSede(crearSedeDto: CrearSedeDto): Observable<CrearSedeDto> {

    return this.http.post(this.administradorEndPoint + 'asignarVlanSede',
      crearSedeDto, { responseType: 'json' }).pipe(
        map((respuesta) => respuesta as CrearSedeDto),
      );
  }
  asignarDispositivoVlan(asignacion: Vlan[]): Observable<Asignacion[]> {

    return this.http.post(this.administradorEndPoint + 'asignarIpVlanSede',
      asignacion, { responseType: 'json' }).pipe(
        map((respuesta) => respuesta as Asignacion[]),
      );
  }

  crearSuperNet(parametros: SuperNet): Observable<SuperNet> {
    return this.http.post(this.administradorEndPoint + 'crearSuperNet',
      parametros, { responseType: 'json' }).pipe(
        map((resultado) => resultado as SuperNet)
      );
  }
  validarRangoSupernet(parametro: number): Observable<SuperNet> {
    return this.http.post(this.administradorEndPoint + 'validarRangoSupernet',
    parametro, { responseType: 'json' }).pipe(
        map((resultado) => resultado as SuperNet)
      );
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/administrador-ip.service.ts